'use client';

import { c as _c } from "react/compiler-runtime";
import { Xmark } from '@byway/icons';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import { Button } from '~/components/Button';
import { usePushToMigratedRoute } from '~/hooks/usePushToMigratedRoute';
import { useRouter } from '~/i18n/navigation';
import { usePlaygroundActions, usePlaygroundAllCmsPlaces, usePlaygroundContextParams, usePlaygroundNights } from '~/stores/playgroundStore';
import styles from './PlaygroundDialog.module.css';
const DIALOG_TRACKING = {
  'select-dates-first': {
    cancelTracking: 'pg-cancel-select-dates-first',
    confirmTracking: 'pg-confirm-select-dates-first'
  },
  'change-origin': {
    cancelTracking: 'pg-cancel-change-origin',
    confirmTracking: 'pg-confirm-change-origin'
  },
  'results-filtered': {
    cancelTracking: null,
    confirmTracking: 'pg-confirm-results-filtered'
  },
  'lose-trips': {
    cancelTracking: 'pg-cancel-lose-trips',
    confirmTracking: 'pg-confirm-lose-trips'
  },
  'edit-journey': {
    cancelTracking: null,
    confirmTracking: 'pg-confirm-edit-journey'
  },
  'couldnt-create-trip': {
    cancelTracking: null,
    confirmTracking: 'pg-confirm-contact'
  }
} as const;
type PlaygroundDialogType = keyof typeof DIALOG_TRACKING;

// Translation keys are called as literals (not composed from `variant`) so the repo's
// i18n:check static-usage validator — which greps source for literal t('...') calls and
// can't follow dynamic keys — can see each one is actually referenced.
function getDialogContent(t: ReturnType<typeof useTranslations<'playground'>>, variant: PlaygroundDialogType) {
  switch (variant) {
    case 'select-dates-first':
      return {
        title: t('dialog.selectDatesFirst.title'),
        body: t('dialog.selectDatesFirst.body'),
        cancelText: t('dialog.selectDatesFirst.cancel'),
        confirmText: t('dialog.selectDatesFirst.confirm')
      };
    case 'change-origin':
      return {
        title: t('dialog.changeOrigin.title'),
        body: t('dialog.changeOrigin.body'),
        cancelText: t('dialog.changeOrigin.cancel'),
        confirmText: t('dialog.changeOrigin.confirm')
      };
    case 'results-filtered':
      return {
        title: t('dialog.resultsFiltered.title'),
        body: t('dialog.resultsFiltered.body'),
        cancelText: null,
        confirmText: t('dialog.resultsFiltered.confirm')
      };
    case 'lose-trips':
      return {
        title: t('dialog.loseTrips.title'),
        body: t('dialog.loseTrips.body'),
        cancelText: t('dialog.loseTrips.cancel'),
        confirmText: t('dialog.loseTrips.confirm')
      };
    case 'edit-journey':
      return {
        title: t('dialog.editJourney.title'),
        body: t('dialog.editJourney.body'),
        cancelText: null,
        confirmText: t('dialog.editJourney.confirm')
      };
    case 'couldnt-create-trip':
      return {
        title: t('dialog.couldntCreateTrip.title'),
        body: t('dialog.couldntCreateTrip.body'),
        cancelText: null,
        confirmText: t('dialog.couldntCreateTrip.confirm')
      };
  }
}
type PlaygroundDialogProps = {
  isOpen: boolean;
  variant: PlaygroundDialogType;
  onClose: () => void;
  onConfirm?: () => void;
  confirmTracking?: string;
};
function PlaygroundDialog(t0) {
  const $ = _c(49);
  const {
    isOpen,
    variant,
    onClose,
    onConfirm: t1
  } = t0;
  const onConfirm = t1 === undefined ? onClose : t1;
  const router = useRouter();
  const pushToMigratedRoute = usePushToMigratedRoute();
  const t = useTranslations("playground");
  const aria = useTranslations("aria");
  let t2;
  if ($[0] !== t || $[1] !== variant) {
    t2 = getDialogContent(t, variant);
    $[0] = t;
    $[1] = variant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = DIALOG_TRACKING[variant];
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      ...t2,
      ...t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dialog = t4;
  const {
    openDrawer,
    resetState,
    setIsLoading
  } = usePlaygroundActions();
  const nights = usePlaygroundNights();
  const {
    originId,
    startDate
  } = usePlaygroundContextParams();
  const allCmsPlaces = usePlaygroundAllCmsPlaces();
  const hasCmsPlaces = allCmsPlaces.length > 0;
  let t5;
  if ($[6] !== onClose) {
    t5 = () => {
      onClose();
    };
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleClose = t5;
  let t6;
  if ($[8] !== hasCmsPlaces || $[9] !== nights || $[10] !== onConfirm || $[11] !== openDrawer || $[12] !== originId || $[13] !== pushToMigratedRoute || $[14] !== resetState || $[15] !== router || $[16] !== setIsLoading || $[17] !== startDate || $[18] !== variant) {
    t6 = async () => {
      onConfirm();
      if (variant === "change-origin") {
        setIsLoading(true);
        pushToMigratedRoute("/map");
        await new Promise(_temp);
        resetState();
      }
      if (variant === "select-dates-first") {
        openDrawer("dates");
      }
      if (variant === "lose-trips") {
        setIsLoading(true);
        const baseUrl = `/map/build?origin=${originId}&nights=${nights}`;
        if (startDate) {
          const firstParam = hasCmsPlaces ? "" : "&first=true";
          router.back();
          router.replace(`${baseUrl}${firstParam}`);
        } else {
          router.back();
          router.replace(baseUrl);
        }
      }
      if (variant === "couldnt-create-trip") {
        router.push("/contact");
      }
    };
    $[8] = hasCmsPlaces;
    $[9] = nights;
    $[10] = onConfirm;
    $[11] = openDrawer;
    $[12] = originId;
    $[13] = pushToMigratedRoute;
    $[14] = resetState;
    $[15] = router;
    $[16] = setIsLoading;
    $[17] = startDate;
    $[18] = variant;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleConfirm = t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TransitionChild as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"><div className={styles.overlay} /></TransitionChild>;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== aria) {
    t8 = aria("close");
    $[21] = aria;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <Xmark size="large" ariaLabel={t8} />;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== handleClose || $[26] !== t9) {
    t10 = <div className={styles.header}><button type="button" onClick={handleClose}>{t9}</button></div>;
    $[25] = handleClose;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== dialog.title) {
    t11 = <DialogTitle className={styles.dialogTitle}>{dialog.title}</DialogTitle>;
    $[28] = dialog.title;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== dialog.body) {
    t12 = <p className={styles.dialogBodyText}>{dialog.body}</p>;
    $[30] = dialog.body;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== dialog.confirmText || $[33] !== dialog.confirmTracking || $[34] !== handleConfirm) {
    t13 = <div className={styles.dialogButtonContainer}><Button type="button" onPress={handleConfirm} data-ua-click={dialog.confirmTracking} variant="primary" size="fullWidth">{dialog.confirmText}</Button></div>;
    $[32] = dialog.confirmText;
    $[33] = dialog.confirmTracking;
    $[34] = handleConfirm;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <main className={styles.main}>{t11}{t12}{t13}</main>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t10 || $[41] !== t14) {
    t15 = <div className={styles.dialogPanelCentered}><div className={styles.dialogPanelContainer}><TransitionChild as="div" enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"><DialogPanel className={styles.dialogPanel}>{t10}{t14}</DialogPanel></TransitionChild></div></div>;
    $[40] = t10;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== handleClose || $[44] !== t15) {
    t16 = <Dialog as="div" className={styles.dialog} onClose={handleClose}>{t7}{t15}</Dialog>;
    $[43] = handleClose;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== isOpen || $[47] !== t16) {
    t17 = <Transition appear={true} show={isOpen} as="div">{t16}</Transition>;
    $[46] = isOpen;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
}
function _temp(resolve) {
  return setTimeout(resolve, 1000);
}
export { PlaygroundDialog, type PlaygroundDialogType };